import { TMoovBlockRef } from 'features/BankAccount/types'
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState
} from 'react'
import { useAppDispatch } from 'redux/createStore'
import { setLoading } from 'redux/store/common/slice'
import { notification } from 'components/Notification'
import { loadMoov } from '@moovio/moov-js'
import {
  completeOnboardingSession,
  continueOnboardingSession,
  getMoovToken
} from './api'
import { AxiosResponse } from 'axios'
import { TCommunityEntity } from './types'

type TPendingMoovSession = {
  token: string
  foreignId: string
}

export const MyCommunityMoovForm = ({
  openForm,
  onCancel,
  onSuccessCallback,
  predefinedAccountId,
  predefinedOnboadingSessionUuid,
  communityEntity
}: {
  openForm: boolean
  onCancel: () => void
  onSuccessCallback: () => void
  predefinedAccountId?: string
  predefinedOnboadingSessionUuid?: string
  communityEntity: TCommunityEntity
}) => {
  const dispatch = useAppDispatch()
  const [accessToken, setAccessToken] = useState<string | null>(null)
  const [showMoovHost, setShowMoovHost] = useState(false)

  const moovBlockRef = useRef<TMoovBlockRef>(null)
  const prepareGenerationRef = useRef(0)
  const pendingSessionRef = useRef<TPendingMoovSession | null>(null)
  const openDropRef = useRef<
    ((config: Record<string, unknown>, elementId: string) => void) | null
  >(null)

  const moovFormKey = useMemo(() => Math.random(), [])

  const onboadingSessionUuidRef = useRef<string | undefined>(
    predefinedOnboadingSessionUuid
  )

  useEffect(() => {
    onboadingSessionUuidRef.current = predefinedOnboadingSessionUuid
  }, [predefinedOnboadingSessionUuid])

  const refreshOnboardingResourceToken = useCallback(
    async (createdAccountId: string) => {
      const sessionUuid = onboadingSessionUuidRef.current
      if (!sessionUuid) return
      const res = await continueOnboardingSession(createdAccountId, sessionUuid)
      if (res.data?.access_token) {
        setAccessToken(res.data.access_token)
        return res.data.access_token
      }
      return null
    },
    []
  )

  const onResourceCreated = useCallback(
    async ({
      resourceType,
      resource
    }: {
      resourceType: string
      resource: { accountID?: string; accountId?: string }
    }) => {
      if (resourceType !== 'account') return

      const createdAccountId = resource.accountID ?? resource.accountId
      if (!createdAccountId) return
      const newAccessToken = await refreshOnboardingResourceToken(
        createdAccountId
      )
      if (newAccessToken && moovBlockRef.current) {
        moovBlockRef.current.token = newAccessToken
      }

      // Backend should mint a new token scoped to this created account
      // (accounts/{newAccountId}/profile.*, representatives.*, files.*, etc.)
      // then update onboarding token with it.
    },
    [refreshOnboardingResourceToken]
  )

  const onSuccess = useCallback(async () => {
    const sessionUuid = onboadingSessionUuidRef.current
    if (!sessionUuid) return
    if (moovBlockRef.current) {
      moovBlockRef.current.open = false
    }
    await completeOnboardingSession(sessionUuid)
    onSuccessCallback()
  }, [onSuccessCallback])

  const prepareMoovOnboarding = useCallback(
    async (generation: number) => {
      try {
        dispatch(setLoading(true))
        const sessionUuid = onboadingSessionUuidRef.current
        const moovToken =
          predefinedAccountId && sessionUuid
            ? await continueOnboardingSession(predefinedAccountId, sessionUuid)
            : await getMoovToken(communityEntity).then((res) => {
                onboadingSessionUuidRef.current = res.data.uuid
                return res
              })
        if (generation !== prepareGenerationRef.current) return

        if (!moovToken?.data?.access_token) {
          notification.error({ message: 'Unable to get Moov token' })
          return
        }

        const moovClient = await loadMoov(moovToken?.data.access_token)
        if (generation !== prepareGenerationRef.current) return

        if (!moovClient) {
          notification.error({ message: 'Unable to initialize Moov SDK' })
          return
        }
        const drops = await moovClient.drops()
        if (generation !== prepareGenerationRef.current) return

        const openDrop = (
          drops as unknown as { onboarding?: (...args: unknown[]) => void }
        ).onboarding
        if (!openDrop) {
          notification.error({
            message: 'Moov onboarding drop is unavailable in current SDK'
          })
          return
        }

        if (generation !== prepareGenerationRef.current) return

        pendingSessionRef.current = {
          token: moovToken.data.access_token,
          foreignId: moovToken.data.foreign_id
        }
        openDropRef.current = openDrop as (
          config: Record<string, unknown>,
          elementId: string
        ) => void

        setAccessToken(moovToken.data.access_token)
        setShowMoovHost(true)
      } catch (error) {
        const typedError = error as AxiosResponse
        console.error(typedError)
        if (typedError.data?.non_field_errors) {
          notification.error({ message: typedError.data.non_field_errors[0] })
        }
        onCancel()
      } finally {
        dispatch(setLoading(false))
      }
    },
    [dispatch, predefinedAccountId, communityEntity, onCancel]
  )

  useLayoutEffect(() => {
    if (!showMoovHost || !accessToken) return

    const openDrop = openDropRef.current
    const pending = pendingSessionRef.current
    if (!openDrop || !pending) return

    const el = moovBlockRef.current
    if (!el?.id) return

    openDrop(
      {
        token: pending.token,
        accountID: predefinedAccountId,
        accountTypes: ['business'],
        accountData: {
          foreignID: pending.foreignId
        },
        paymentMethodTypes: [],
        capabilities: [
          'wallet.balance',
          'transfers',
          'send-funds.ach',
          'collect-funds.ach'
        ],
        onResourceCreated,
        onSuccess,
        onError: (args) => {
          console.error(args)
        },
        onCancel: () => {
          if (moovBlockRef.current) {
            onCancel()
          }
        }
      },
      el.id
    )
    el.open = true

    openDropRef.current = null
    pendingSessionRef.current = null
  }, [
    showMoovHost,
    accessToken,
    onResourceCreated,
    onSuccess,
    onCancel,
    predefinedAccountId
  ])

  useEffect(() => {
    if (!openForm) {
      prepareGenerationRef.current += 1
      onboadingSessionUuidRef.current = predefinedOnboadingSessionUuid
      setShowMoovHost(false)
      setAccessToken(null)
      pendingSessionRef.current = null
      openDropRef.current = null
      if (moovBlockRef.current) {
        moovBlockRef.current.open = false
      }
      return
    }

    const generation = ++prepareGenerationRef.current
    void prepareMoovOnboarding(generation)

    return () => {
      prepareGenerationRef.current += 1
    }
  }, [openForm, prepareMoovOnboarding, predefinedOnboadingSessionUuid])

  return (
    <div>
      {showMoovHost && accessToken ? (
        <>
          {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
          {/* @ts-ignore */}
          <moov-onboarding
            accessToken={accessToken}
            ref={moovBlockRef}
            id="moov-business-information"
            key={moovFormKey}
          />
        </>
      ) : null}
    </div>
  )
}
